import React, { useEffect, useRef, useState } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  Bell,
  CheckCircle,
  ChevronUp,
  Info,
  LogOut,
  Moon,
  PanelLeftOpen,
  PanelRightOpen,
  Settings,
  Sun,
  UserCircle,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from './Theme';

type NotificationType = 'info' | 'success' | 'warning' | 'error';

interface NotificationItem {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  time: string;
  read?: boolean;
}

const NOTIFICATION_CONFIG: Record<
  NotificationType,
  { icon: React.ElementType; bgClass: string; iconClass: string; borderClass: string }
> = {
  info: { icon: Info, bgClass: 'bg-blue-500/10', iconClass: 'text-blue-400', borderClass: 'border-l-blue-500' },
  success: { icon: CheckCircle, bgClass: 'bg-emerald-500/10', iconClass: 'text-emerald-400', borderClass: 'border-l-emerald-500' },
  warning: { icon: AlertTriangle, bgClass: 'bg-amber-500/10', iconClass: 'text-amber-400', borderClass: 'border-l-amber-500' },
  error: { icon: AlertCircle, bgClass: 'bg-red-500/10', iconClass: 'text-red-400', borderClass: 'border-l-red-500' },
};

const SAMPLE_NOTIFICATIONS: NotificationItem[] = [
  { id: '1', type: 'success', title: 'Ingestion completed', message: 'Data model "Sales CRM" finished syncing successfully.', time: '2 min ago', read: false },
  { id: '2', type: 'warning', title: 'Schedule delayed', message: 'Nightly job was delayed by 15 minutes due to high load.', time: '1 hour ago', read: true },
  { id: '3', type: 'info', title: 'New update available', message: 'Portal update v2.4 is ready to install.', time: '3 hours ago', read: true },
  { id: '4', type: 'error', title: 'Connection failed', message: 'Database connection "Warehouse" timed out.', time: 'Yesterday', read: false },
];

interface SidebarFooterProps {
  collapsed: boolean;
  setCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
}

function getUserDisplayName(): string {
  return localStorage.getItem('user_name')?.trim() || 'User';
}

function getUserInitials(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

const SidebarFooter: React.FC<SidebarFooterProps> = ({ collapsed, setCollapsed }) => {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const isLight = theme === 'white';

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications] = useState<NotificationItem[]>(SAMPLE_NOTIFICATIONS);

  const footerRef = useRef<HTMLDivElement>(null);
  const unreadCount = notifications.filter((n) => !n.read).length;
  const userName = getUserDisplayName();
  const userInitials = getUserInitials(userName);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (footerRef.current && !footerRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
        setNotifOpen(false);
      }
    };
    if (userMenuOpen || notifOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [userMenuOpen, notifOpen]);

  const logout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const popoverPosition = collapsed
    ? 'left-full bottom-0 ml-2'
    : 'left-0 right-0 bottom-full mb-2';

  const surfaceClass = isLight
    ? 'bg-white border border-neutral-200 shadow-lg ring-1 ring-black/5'
    : 'bg-neutral-900 border border-neutral-700 shadow-xl ring-1 ring-white/5';

  const itemHover = isLight ? 'hover:bg-neutral-100' : 'hover:bg-neutral-800';
  const mutedText = isLight ? 'text-neutral-500' : 'text-neutral-400';
  const primaryText = isLight ? 'text-neutral-900' : 'text-white';

  const iconBtnClass = (active = false) =>
    `relative flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-colors focus:outline-none ${active
      ? isLight ? 'bg-indigo-50 text-indigo-600' : 'bg-indigo-500/15 text-indigo-400'
      : isLight ? 'text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900' : 'text-neutral-400 hover:bg-neutral-800 hover:text-white'
    }`;

  const toolbarClass = isLight
    ? 'border-neutral-200 bg-white shadow-sm'
    : 'border-neutral-800 bg-neutral-900/80';

  return (
    <div ref={footerRef} className={`relative shrink-0 border-t p-2 ${isLight ? 'border-neutral-200 bg-neutral-50/80' : 'border-neutral-800 bg-neutral-950/90'}`}>
      {/* Notifications popover */}
      {notifOpen && (
        <div
          className={`absolute z-50 flex max-h-[min(420px,50vh)] w-[min(380px,calc(100vw-2rem))] flex-col overflow-hidden rounded-xl ${surfaceClass} ${popoverPosition}`}
        >
          <div className={`flex items-center justify-between border-b px-4 py-3 ${isLight ? 'border-neutral-200' : 'border-neutral-800'}`}>
            <h3 className={`text-sm font-semibold ${primaryText}`}>Notifications</h3>
            {unreadCount > 0 && <span className={`text-xs ${mutedText}`}>{unreadCount} unread</span>}
          </div>
          <div className="overflow-y-auto flex-1">
            <ul className="py-1">
              {notifications.map((n) => {
                const config = NOTIFICATION_CONFIG[n.type];
                const Icon = config.icon;
                return (
                  <li
                    key={n.id}
                    className={`flex gap-3 border-l-4 px-4 py-3 transition-colors cursor-pointer ${config.borderClass} ${n.read ? (isLight ? 'bg-neutral-50' : 'bg-neutral-900/50') : config.bgClass} ${itemHover}`}
                  >
                    <span className={`shrink-0 mt-0.5 ${config.iconClass}`}>
                      <Icon className="w-4 h-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm font-medium ${n.read ? mutedText : primaryText}`}>{n.title}</p>
                      <p className={`text-xs mt-0.5 line-clamp-2 ${mutedText}`}>{n.message}</p>
                      <p className={`text-[10px] mt-1 ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`}>{n.time}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}

      {/* User menu popover */}
      {userMenuOpen && (
        <div className={`absolute z-50 w-56 overflow-hidden rounded-xl py-1 ${surfaceClass} ${popoverPosition}`}>
          <button
            type="button"
            onClick={() => { navigate('/portal/my-account'); setUserMenuOpen(false); }}
            className={`flex w-full items-center gap-2 px-3 py-2 text-sm ${isLight ? 'text-neutral-700' : 'text-neutral-200'} ${itemHover}`}
          >
            <UserCircle className="w-4 h-4 shrink-0" />
            My profile
          </button>
          <button
            type="button"
            onClick={() => { navigate('/portal/system-settings'); setUserMenuOpen(false); }}
            className={`flex w-full items-center gap-2 px-3 py-2 text-sm ${isLight ? 'text-neutral-700' : 'text-neutral-200'} ${itemHover}`}
          >
            <Settings className="w-4 h-4 shrink-0" />
            Settings
          </button>
          <div className={`mx-3 my-2 rounded-lg border p-1 flex gap-1 ${isLight ? 'border-neutral-200 bg-neutral-50' : 'border-neutral-800 bg-neutral-950'}`}>
            <button
              type="button"
              onClick={() => setTheme('black')}
              className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${theme === 'black'
                ? isLight ? 'bg-white text-neutral-900 shadow-sm' : 'bg-neutral-800 text-white'
                : isLight ? 'text-neutral-500 hover:text-neutral-900' : 'text-neutral-400 hover:text-white'
                }`}
            >
              <Moon className="w-3.5 h-3.5" />
              Dark
            </button>
            <button
              type="button"
              onClick={() => setTheme('white')}
              className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${theme === 'white'
                ? isLight ? 'bg-white text-neutral-900 shadow-sm' : 'bg-neutral-800 text-white'
                : isLight ? 'text-neutral-500 hover:text-neutral-900' : 'text-neutral-400 hover:text-white'
                }`}
            >
              <Sun className="w-3.5 h-3.5" />
              Light
            </button>
          </div>
          <button
            type="button"
            onClick={logout}
            className={`flex w-full items-center gap-2 px-3 py-2 text-sm text-red-500 ${isLight ? 'hover:bg-red-50' : 'hover:bg-red-950/30'}`}
          >
            <LogOut className="w-4 h-4 shrink-0" />
            Logout
          </button>
        </div>
      )}

      <div className={`flex flex-col gap-2 ${collapsed ? 'items-center' : ''}`}>
        {/* Notifications + collapse — same row */}
        <div className={`flex w-full ${collapsed ? 'justify-center' : ''}`}>
          <div className={`flex w-full items-center rounded-lg border p-0.5 ${toolbarClass} ${collapsed ? 'w-auto' : ''}`}>
            <button
              type="button"
              onClick={() => { setNotifOpen((v) => !v); setUserMenuOpen(false); }}
              className={`relative flex min-w-0 flex-1 items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors focus:outline-none ${notifOpen
                ? isLight ? 'bg-indigo-50 text-indigo-700' : 'bg-indigo-500/15 text-indigo-300'
                : isLight ? 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900' : 'text-neutral-300 hover:bg-neutral-800 hover:text-white'
                } ${collapsed ? 'flex-none px-2' : ''}`}
              aria-label="Notifications"
              title="Notifications"
            >
              <Bell className="h-4 w-4 shrink-0" />
              {!collapsed && <span className="truncate text-xs font-medium">Notifications</span>}
              {unreadCount > 0 && !collapsed && (
                <span className="ml-auto shrink-0 rounded-full bg-indigo-500 px-1.5 py-0.5 text-[10px] font-bold text-white tabular-nums">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
              {unreadCount > 0 && collapsed && (
                <span className={`absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-indigo-500 px-1 text-[9px] font-bold text-white ring-2 ${isLight ? 'ring-white' : 'ring-neutral-900'}`}>
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
            <span className={`mx-0.5 h-5 w-px shrink-0 ${isLight ? 'bg-neutral-200' : 'bg-neutral-700'}`} aria-hidden />
            <button
              type="button"
              onClick={() => setCollapsed((prev) => !prev)}
              className={iconBtnClass()}
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelRightOpen className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {/* User */}
        <button
          type="button"
          onClick={() => { setUserMenuOpen((v) => !v); setNotifOpen(false); }}
          className={`flex items-center transition-colors rounded-lg ${collapsed
            ? 'p-0.5'
            : `w-full gap-2.5 px-2 py-2 text-left ${isLight ? 'hover:bg-neutral-200/80' : 'hover:bg-neutral-800/80'}`
            }`}
          aria-expanded={userMenuOpen}
          aria-haspopup="true"
        >
          <span
            className={`flex shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 text-[10px] font-bold text-white ${collapsed ? 'h-8 w-8' : 'h-8 w-8'}`}
          >
            {userInitials}
          </span>
          {!collapsed && (
            <>
              <span className="min-w-0 flex-1">
                <span className={`block truncate text-xs font-semibold ${primaryText}`}>{userName}</span>
                <span className={`block truncate text-[10px] ${mutedText}`}>Account & settings</span>
              </span>
              <ChevronUp className={`w-3.5 h-3.5 shrink-0 transition-transform ${mutedText} ${userMenuOpen ? '' : 'rotate-180'}`} />
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default SidebarFooter;
